import React, { useState, useEffect, Suspense } from 'react';
import { ArrowLeft, Save, X, Search, Check, Volume2, Mic } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { expertApi } from '../services/expertApi';
import type { BankerPreferences, Exchange, TTSVoice } from '../services/expertApi';
import { tradingApi } from '../services/tradingApi';
import type { TradingConfig } from '../types/trading';
import BankerAvatar from '../components/avatar/BankerAvatar';
import { PermissionGate } from '../components/PermissionGate';
import { UpgradeNudge } from '../components/UpgradeNudge';
import { usePermission } from '../hooks/usePermission';

// Lazy-load 3D avatar for settings preview (AC7: shows 3D in idle state at sm size)
const BankerAvatar3D = React.lazy(() => import('../components/avatar/BankerAvatar3D'));

const AVAILABLE_SECTORS = [
  'Mining', 'Banks', 'Tech/Media', 'Retail', 'Telecoms', 'Energy',
  'Property', 'Technology', 'Healthcare', 'Financials', 'Consumer', 'Industrial',
];

const DEFAULT_EXCHANGES = ['JSE', 'US', 'LSE', 'XETRA', 'TO', 'AU'];

const BankerSettings: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: prefs, isLoading } = useQuery({
    queryKey: ['banker-preferences'],
    queryFn: () => expertApi.getBankerPreferences(),
  });

  const { data: exchanges } = useQuery({
    queryKey: ['exchanges'],
    queryFn: () => expertApi.getExchanges(),
  });

  const { data: voices } = useQuery({
    queryKey: ['tts-voices'],
    queryFn: () => expertApi.getTTSVoices(),
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<BankerPreferences>) => expertApi.updateBankerPreferences(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['banker-preferences'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
  });

  const [form, setForm] = useState<BankerPreferences>({
    banker_enabled: false,
    banker_sectors: null,
    banker_exchanges: null,
    banker_max_leads_per_cycle: 3,
    banker_max_daily_notifications: 5,
    banker_lead_archive_days: 7,
    banker_analyst_name: 'Market Analyst',
    banker_advisor_name: 'Investment Banker',
    banker_analyst_voice: null,
    banker_advisor_voice: null,
    banker_voice_autoplay: false,
    banker_avatar_enabled: false,
    teams_webhook_url: null,
    teams_notify_leads: true,
    teams_notify_alerts: true,
    discord_webhook_url: null,
    discord_notify_leads: true,
    discord_notify_alerts: true,
    discord_leads_webhook_url: null,
    discord_alerts_webhook_url: null,
    teams_leads_webhook_url: null,
    teams_alerts_webhook_url: null,
  });
  const [showTeamsUrl, setShowTeamsUrl] = useState(false);
  const [showDiscordUrl, setShowDiscordUrl] = useState(false);
  const [showTeamsLeadsUrl, setShowTeamsLeadsUrl] = useState(false);
  const [showTeamsAlertsUrl, setShowTeamsAlertsUrl] = useState(false);
  const [showDiscordLeadsUrl, setShowDiscordLeadsUrl] = useState(false);
  const [showDiscordAlertsUrl, setShowDiscordAlertsUrl] = useState(false);
  const [saved, setSaved] = useState(false);
  const [ipoSaved, setIpoSaved] = useState(false);
  const [exchangeSearch, setExchangeSearch] = useState('');
  const [showExchangePicker, setShowExchangePicker] = useState(false);

  // IPO admin config (admin-only)
  const ALL_IPO_PROVIDERS = ['eodhd', 'finnhub', 'cryptorank', 'coingecko', 'dexscreener'] as const;

  const PROVIDER_INFO: Record<string, { needsKey: boolean; keyField?: string; hint: string }> = {
    eodhd: { needsKey: false, hint: 'Uses existing EODHD subscription' },
    finnhub: { needsKey: true, keyField: 'finnhub_api_key', hint: 'Free tier: 60 calls/min' },
    cryptorank: { needsKey: true, keyField: 'cryptorank_api_key', hint: 'Free tier available' },
    coingecko: { needsKey: true, keyField: 'coingecko_api_key', hint: 'Free tier (no key needed), Pro key for higher limits' },
    dexscreener: { needsKey: false, hint: 'Free — no API key required' },
  };

  const { data: adminConfig } = useQuery({
    queryKey: ['trading-config'],
    queryFn: () => tradingApi.getConfig(),
  });

  const [ipoForm, setIpoForm] = useState({
    ipo_providers_enabled: ['eodhd', 'coingecko', 'dexscreener'] as string[],
    ipo_stock_enabled: true,
    ipo_crypto_enabled: true,
    ipo_cache_ttl_hours: 6,
    finnhub_api_key: '',
    cryptorank_api_key: '',
    coingecko_api_key: '',
  });

  useEffect(() => {
    if (adminConfig) {
      setIpoForm({
        ipo_providers_enabled: adminConfig.ipo_providers_enabled ?? ['eodhd', 'coingecko', 'dexscreener'],
        ipo_stock_enabled: adminConfig.ipo_stock_enabled ?? true,
        ipo_crypto_enabled: adminConfig.ipo_crypto_enabled ?? true,
        ipo_cache_ttl_hours: adminConfig.ipo_cache_ttl_hours ?? 6,
        finnhub_api_key: (adminConfig as Record<string, unknown>).finnhub_api_key as string ?? '',
        cryptorank_api_key: (adminConfig as Record<string, unknown>).cryptorank_api_key as string ?? '',
        coingecko_api_key: (adminConfig as Record<string, unknown>).coingecko_api_key as string ?? '',
      });
    }
  }, [adminConfig]);

  const ipoConfigMutation = useMutation({
    mutationFn: (data: Partial<TradingConfig>) => tradingApi.updateConfig(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trading-config'] });
      setIpoSaved(true);
      setTimeout(() => setIpoSaved(false), 2000);
    },
  });

  const handleIpoSave = () => {
    // Don't send masked keys back — only send if user entered a new value
    const payload: Record<string, unknown> = {
      ipo_providers_enabled: ipoForm.ipo_providers_enabled,
      ipo_stock_enabled: ipoForm.ipo_stock_enabled,
      ipo_crypto_enabled: ipoForm.ipo_crypto_enabled,
      ipo_cache_ttl_hours: ipoForm.ipo_cache_ttl_hours,
    };
    if (ipoForm.finnhub_api_key && !ipoForm.finnhub_api_key.startsWith('••••')) {
      payload.finnhub_api_key = ipoForm.finnhub_api_key;
    }
    if (ipoForm.cryptorank_api_key && !ipoForm.cryptorank_api_key.startsWith('••••')) {
      payload.cryptorank_api_key = ipoForm.cryptorank_api_key;
    }
    if (ipoForm.coingecko_api_key && !ipoForm.coingecko_api_key.startsWith('••••')) {
      payload.coingecko_api_key = ipoForm.coingecko_api_key;
    }
    ipoConfigMutation.mutate(payload as Partial<TradingConfig>);
  };

  const toggleIpoProvider = (provider: string) => {
    setIpoForm(prev => ({
      ...prev,
      ipo_providers_enabled: prev.ipo_providers_enabled.includes(provider)
        ? prev.ipo_providers_enabled.filter(p => p !== provider)
        : [...prev.ipo_providers_enabled, provider],
    }));
  };
  const [previewingVoice, setPreviewingVoice] = useState<string | null>(null);

  // Determine which avatar tier this user has (AC7)
  const { allowed: has3DAvatar } = usePermission('feature:banker-avatar-3d');

  // Voice Input settings (Story 7.3) — device preferences stored in localStorage
  const [voiceInputEnabled, setVoiceInputEnabledState] = useState<boolean>(() => {
    try { return localStorage.getItem('voice_input_enabled') === 'true'; } catch { return false; }
  });
  const [voiceAutoSend, setVoiceAutoSendState] = useState<boolean>(() => {
    try { return localStorage.getItem('voice_autosend') === 'true'; } catch { return false; }
  });
  const [voiceHotkey, setVoiceHotkeyState] = useState<string>(() => {
    try { return localStorage.getItem('voice_hotkey') || 'Space'; } catch { return 'Space'; }
  });

  const setVoiceInputEnabled = (val: boolean) => {
    setVoiceInputEnabledState(val);
    try { localStorage.setItem('voice_input_enabled', String(val)); } catch { /* ignore */ }
  };
  const setVoiceAutoSend = (val: boolean) => {
    setVoiceAutoSendState(val);
    try { localStorage.setItem('voice_autosend', String(val)); } catch { /* ignore */ }
  };
  const setVoiceHotkey = (val: string) => {
    setVoiceHotkeyState(val);
    try { localStorage.setItem('voice_hotkey', val); } catch { /* ignore */ }
  };

  useEffect(() => {
    if (prefs) {
      setForm({
        banker_enabled: prefs.banker_enabled,
        banker_sectors: prefs.banker_sectors,
        banker_exchanges: prefs.banker_exchanges ?? DEFAULT_EXCHANGES,
        banker_max_leads_per_cycle: prefs.banker_max_leads_per_cycle,
        banker_max_daily_notifications: prefs.banker_max_daily_notifications,
        banker_lead_archive_days: prefs.banker_lead_archive_days,
        banker_analyst_name: prefs.banker_analyst_name,
        banker_advisor_name: prefs.banker_advisor_name,
        banker_analyst_voice: prefs.banker_analyst_voice,
        banker_advisor_voice: prefs.banker_advisor_voice,
        banker_voice_autoplay: prefs.banker_voice_autoplay,
        banker_avatar_enabled: prefs.banker_avatar_enabled ?? false,
        teams_webhook_url: prefs.teams_webhook_url ?? null,
        teams_notify_leads: prefs.teams_notify_leads ?? true,
        teams_notify_alerts: prefs.teams_notify_alerts ?? true,
        discord_webhook_url: prefs.discord_webhook_url ?? null,
        discord_notify_leads: prefs.discord_notify_leads ?? true,
        discord_notify_alerts: prefs.discord_notify_alerts ?? true,
        discord_leads_webhook_url: prefs.discord_leads_webhook_url ?? null,
        discord_alerts_webhook_url: prefs.discord_alerts_webhook_url ?? null,
        teams_leads_webhook_url: prefs.teams_leads_webhook_url ?? null,
        teams_alerts_webhook_url: prefs.teams_alerts_webhook_url ?? null,
      });
    }
  }, [prefs]);

  const handleSave = () => {
    updateMutation.mutate(form);
  };

  const toggleSector = (sector: string) => {
    const current = form.banker_sectors || [];
    if (current.includes(sector)) {
      const next = current.filter((s) => s !== sector);
      setForm({ ...form, banker_sectors: next.length > 0 ? next : null });
    } else {
      setForm({ ...form, banker_sectors: [...current, sector] });
    }
  };

  const toggleExchange = (code: string) => {
    const current = form.banker_exchanges || DEFAULT_EXCHANGES;
    if (current.includes(code)) {
      const next = current.filter((e) => e !== code);
      setForm({ ...form, banker_exchanges: next.length > 0 ? next : [] });
    } else {
      setForm({ ...form, banker_exchanges: [...current, code] });
    }
  };

  const selectedExchanges = form.banker_exchanges || DEFAULT_EXCHANGES;

  const filteredExchanges = exchanges?.filter((e) => {
    if (!exchangeSearch) return true;
    const q = exchangeSearch.toLowerCase();
    return (
      e.code.toLowerCase().includes(q) ||
      e.name.toLowerCase().includes(q) ||
      e.country.toLowerCase().includes(q)
    );
  }) || [];

  const inputClass =
    'bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white w-full focus:outline-none focus:border-teal-500';

  if (isLoading) {
    return (
      <div className="p-6 flex justify-center">
        <div className="w-6 h-6 border-2 border-teal-500/30 border-t-teal-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <PermissionGate permission="feature:banker" fallback={<div className="p-8"><UpgradeNudge tier="premium" /></div>}>
    <div className="p-6 space-y-6 max-w-3xl pb-20 lg:pb-0">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/trading/settings')}
          className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-white">Banker Settings</h1>
          <p className="text-slate-400 text-sm">Configure your Investment Banker preferences</p>
        </div>
        <button
          onClick={handleSave}
          disabled={updateMutation.isPending}
          className="flex items-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold rounded-lg transition-colors disabled:opacity-50"
        >
          <Save size={16} />
          {saved ? 'Saved!' : 'Save Settings'}
        </button>
      </div>

      {updateMutation.isError && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-sm text-red-300">
          {updateMutation.error?.message || 'Failed to save preferences'}
        </div>
      )}

      {/* Enable/Disable */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5">
        <div className="flex items-center justify-between min-h-[44px]">
          <div>
            <h2 className="text-lg font-semibold text-white">Enable Investment Banker</h2>
            <p className="text-xs text-slate-500 mt-1">
              When enabled, the Banker will monitor your portfolio, discover opportunities, and send
              notifications based on your preferences.
            </p>
          </div>
          <button
            onClick={() => setForm({ ...form, banker_enabled: !form.banker_enabled })}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              form.banker_enabled ? 'bg-teal-500' : 'bg-slate-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                form.banker_enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Persona Names */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 space-y-4">
        <h2 className="text-lg font-semibold text-white">Persona Names</h2>
        <p className="text-xs text-slate-500 -mt-2">
          Customize the names used by the Market Analyst and Investment Banker in notifications.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-slate-400 block mb-1">Market Analyst Name</label>
            <input
              type="text"
              value={form.banker_analyst_name}
              onChange={(e) => setForm({ ...form, banker_analyst_name: e.target.value })}
              placeholder="Market Analyst"
              maxLength={50}
              className={inputClass}
            />
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1">Investment Banker Name</label>
            <input
              type="text"
              value={form.banker_advisor_name}
              onChange={(e) => setForm({ ...form, banker_advisor_name: e.target.value })}
              placeholder="Investment Banker"
              maxLength={50}
              className={inputClass}
            />
          </div>
        </div>
      </div>

      {/* Sector Focus */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-white">Sector Focus</h2>
          <p className="text-xs text-slate-500 mt-1">
            Select which sectors the Banker should scan for opportunities.
            {!form.banker_sectors && ' Currently scanning all sectors.'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {AVAILABLE_SECTORS.map((sector) => {
            const selected = form.banker_sectors === null || form.banker_sectors?.includes(sector);
            return (
              <button
                key={sector}
                onClick={() => toggleSector(sector)}
                className={`px-3 py-2.5 min-h-[44px] flex items-center rounded-full text-sm font-medium transition-colors ${
                  selected
                    ? 'bg-teal-500/20 text-teal-400 border border-teal-500/50'
                    : 'bg-slate-700/50 text-slate-400 border border-slate-600 hover:border-slate-500'
                }`}
              >
                {sector}
              </button>
            );
          })}
        </div>
        {form.banker_sectors && form.banker_sectors.length === 0 && (
          <p className="text-xs text-amber-400">No sectors selected — Banker will not scan any markets.</p>
        )}
      </div>

      {/* Exchange Selection */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white">Exchange Selection</h2>
            <p className="text-xs text-slate-500 mt-1">
              Choose which exchanges to scan. Default: JSE, US, LSE, XETRA, TO, AU.
            </p>
          </div>
          <button
            onClick={() => setShowExchangePicker(!showExchangePicker)}
            className="px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors"
          >
            {showExchangePicker ? 'Done' : 'Edit'}
          </button>
        </div>

        {/* Selected exchanges chips */}
        <div className="flex flex-wrap gap-2">
          {selectedExchanges.map((code) => {
            const ex = exchanges?.find((e) => e.code === code);
            return (
              <div
                key={code}
                className="flex items-center gap-1.5 px-3 py-2 min-h-[44px] bg-teal-500/20 text-teal-400 rounded-full text-sm border border-teal-500/30"
              >
                <span className="font-medium">{code}</span>
                {ex && <span className="text-teal-400/60 text-xs">({ex.country})</span>}
                <button
                  onClick={() => toggleExchange(code)}
                  className="ml-1 hover:text-red-400 transition-colors"
                >
                  <X size={12} />
                </button>
              </div>
            );
          })}
        </div>

        {selectedExchanges.length === 0 && (
          <p className="text-xs text-amber-400">No exchanges selected — Banker will not scan any markets.</p>
        )}

        {/* Exchange picker dropdown */}
        {showExchangePicker && (
          <div className="border border-slate-600 rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-slate-700/50 border-b border-slate-600">
              <div className="relative">
                <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="text"
                  value={exchangeSearch}
                  onChange={(e) => setExchangeSearch(e.target.value)}
                  placeholder="Search by name, code, or country..."
                  className="bg-slate-800 border border-slate-600 rounded px-7 py-1.5 text-sm text-white w-full focus:outline-none focus:border-teal-500"
                />
              </div>
            </div>
            <div className="max-h-60 overflow-y-auto">
              {filteredExchanges.map((ex) => {
                const isSelected = selectedExchanges.includes(ex.code);
                return (
                  <div
                    key={ex.code}
                    onClick={() => toggleExchange(ex.code)}
                    className={`flex items-center justify-between px-3 py-2 min-h-[44px] cursor-pointer transition-colors text-sm ${
                      isSelected
                        ? 'bg-teal-500/10 text-teal-400'
                        : 'text-slate-300 hover:bg-slate-700/50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-mono font-medium w-12">{ex.code}</span>
                      <span>{ex.name}</span>
                      <span className="text-slate-500 text-xs">({ex.country})</span>
                    </div>
                    {isSelected && <Check size={14} className="text-teal-500" />}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Voice Settings (Story 7.1) */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 space-y-4">
        <h2 className="text-lg font-semibold text-white">Voice Settings</h2>
        <p className="text-xs text-slate-500 -mt-2">
          Choose TTS voices for each persona. Requires Premium subscription.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-slate-400 block mb-1">Market Analyst Voice</label>
            <div className="flex gap-2">
              <select
                value={form.banker_analyst_voice || ''}
                onChange={(e) => setForm({ ...form, banker_analyst_voice: e.target.value || null })}
                className={inputClass}
              >
                <option value="">Default</option>
                {voices?.map((v) => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))}
              </select>
              <button
                onClick={async () => {
                  const voice = form.banker_analyst_voice || 'nova';
                  setPreviewingVoice('analyst');
                  try {
                    const res = await expertApi.ttsSpeak('Hello, I am your Market Analyst.', voice);
                    const blob = await res.blob();
                    const url = URL.createObjectURL(blob);
                    const audio = new Audio(url);
                    audio.onended = () => { setPreviewingVoice(null); URL.revokeObjectURL(url); };
                    audio.onerror = () => { setPreviewingVoice(null); URL.revokeObjectURL(url); };
                    await audio.play();
                  } catch {
                    setPreviewingVoice(null);
                  }
                }}
                disabled={previewingVoice === 'analyst'}
                className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors disabled:opacity-50"
                title="Preview voice"
              >
                <Volume2 size={16} className={previewingVoice === 'analyst' ? 'text-teal-400 animate-pulse' : 'text-slate-400'} />
              </button>
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1">Investment Banker Voice</label>
            <div className="flex gap-2">
              <select
                value={form.banker_advisor_voice || ''}
                onChange={(e) => setForm({ ...form, banker_advisor_voice: e.target.value || null })}
                className={inputClass}
              >
                <option value="">Default</option>
                {voices?.map((v) => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))}
              </select>
              <button
                onClick={async () => {
                  const voice = form.banker_advisor_voice || 'nova';
                  setPreviewingVoice('advisor');
                  try {
                    const res = await expertApi.ttsSpeak('Hello, I am your Investment Banker.', voice);
                    const blob = await res.blob();
                    const url = URL.createObjectURL(blob);
                    const audio = new Audio(url);
                    audio.onended = () => { setPreviewingVoice(null); URL.revokeObjectURL(url); };
                    audio.onerror = () => { setPreviewingVoice(null); URL.revokeObjectURL(url); };
                    await audio.play();
                  } catch {
                    setPreviewingVoice(null);
                  }
                }}
                disabled={previewingVoice === 'advisor'}
                className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors disabled:opacity-50"
                title="Preview voice"
              >
                <Volume2 size={16} className={previewingVoice === 'advisor' ? 'text-teal-400 animate-pulse' : 'text-slate-400'} />
              </button>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between pt-2 min-h-[44px]">
          <div>
            <span className="text-sm text-slate-300">Auto-play voice on new notifications</span>
            <p className="text-xs text-slate-500">Automatically speak Banker notifications when received</p>
          </div>
          <button
            onClick={() => setForm({ ...form, banker_voice_autoplay: !form.banker_voice_autoplay })}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              form.banker_voice_autoplay ? 'bg-teal-500' : 'bg-slate-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                form.banker_voice_autoplay ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Voice Input Settings (Story 7.3) */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Mic size={18} className="text-teal-400" />
          <h2 className="text-lg font-semibold text-white">Voice Input</h2>
        </div>
        <p className="text-xs text-slate-500 -mt-2">
          Push-to-talk voice input for Expert Chat. Hold the mic button (or the hotkey) to speak —
          your words are transcribed and placed into the chat input.
        </p>

        {/* Enable voice input toggle */}
        <div className="flex items-center justify-between min-h-[44px]">
          <div>
            <span className="text-sm text-slate-300">Voice input (push-to-talk)</span>
            <p className="text-xs text-slate-500">Show mic button in Expert Chat input</p>
          </div>
          <button
            onClick={() => setVoiceInputEnabled(!voiceInputEnabled)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              voiceInputEnabled ? 'bg-teal-500' : 'bg-slate-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                voiceInputEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {/* Auto-send toggle */}
        <div className="flex items-center justify-between min-h-[44px]">
          <div>
            <span className="text-sm text-slate-300">Auto-send voice messages</span>
            <p className="text-xs text-slate-500">
              Automatically send the transcribed message without pressing Send
            </p>
          </div>
          <button
            onClick={() => setVoiceAutoSend(!voiceAutoSend)}
            disabled={!voiceInputEnabled}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-40 ${
              voiceAutoSend && voiceInputEnabled ? 'bg-teal-500' : 'bg-slate-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                voiceAutoSend && voiceInputEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {/* Hotkey picker */}
        <div>
          <label className="text-xs text-slate-400 block mb-1">Push-to-talk hotkey</label>
          <select
            value={voiceHotkey}
            onChange={(e) => setVoiceHotkey(e.target.value)}
            disabled={!voiceInputEnabled}
            className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white w-full focus:outline-none focus:border-teal-500 disabled:opacity-40"
          >
            <option value="Space">Space</option>
            <option value="V">V</option>
          </select>
          <p className="text-xs text-slate-500 mt-1">
            Active when the chat input is not focused. Use the mic button for mouse/touch.
          </p>
        </div>

        {/* Browser note */}
        <p className="text-xs text-slate-500 border-t border-slate-700 pt-3">
          Chrome, Edge, and Safari use free browser transcription. Firefox uses backend Whisper
          (requires OpenAI API key configured on the server).
        </p>
      </div>

      {/* Avatar Settings (Story 7.4 / v4-9-1) */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 space-y-4">
        <h2 className="text-lg font-semibold text-white">Avatar</h2>
        <p className="text-xs text-slate-500 -mt-2">
          The avatar appears when the Banker sends notifications or during voice conversations.
        </p>
        <div className="flex items-center justify-between min-h-[44px]">
          <div>
            <span className="text-sm text-slate-300">Show Banker avatar</span>
            {has3DAvatar ? (
              <p className="text-xs text-slate-500">3D Avatar (Premium) — display the 3D character during Banker activity</p>
            ) : (
              <p className="text-xs text-slate-500">Display the animated 2D character during Banker activity</p>
            )}
          </div>
          <button
            onClick={() => setForm({ ...form, banker_avatar_enabled: !form.banker_avatar_enabled })}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              form.banker_avatar_enabled ? 'bg-teal-500' : 'bg-slate-600'
            }`}
            aria-checked={form.banker_avatar_enabled}
            role="switch"
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                form.banker_avatar_enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {/* Live preview */}
        {form.banker_avatar_enabled && (
          <div className="flex flex-col items-center gap-3 pt-2">
            <p className="text-xs text-slate-500 self-start">
              Preview (idle state){has3DAvatar ? ' — 3D Avatar (Premium)' : ''}:
            </p>
            {has3DAvatar ? (
              /* AC7: 3D preview for eligible users */
              <div className="flex flex-wrap items-center justify-center gap-6 w-full mx-auto h-48 lg:h-64">
                <div className="flex flex-col items-center gap-1">
                  <Suspense fallback={<BankerAvatar state="idle" persona="analyst" size="sm" />}>
                    <BankerAvatar3D state="idle" persona="analyst" size="sm" />
                  </Suspense>
                  <span className="text-xs text-slate-400">Analyst</span>
                </div>
                <div className="flex flex-col items-center gap-1">
                  <Suspense fallback={<BankerAvatar state="idle" persona="banker" size="sm" />}>
                    <BankerAvatar3D state="idle" persona="banker" size="sm" />
                  </Suspense>
                  <span className="text-xs text-slate-400">Banker</span>
                </div>
              </div>
            ) : (
              /* Standard 2D preview */
              <div className="flex flex-wrap items-center justify-center gap-4 w-full mx-auto">
                <div className="flex flex-col items-center gap-1">
                  <BankerAvatar state="idle" persona="analyst" size="sm" />
                  <span className="text-xs text-slate-400">Analyst</span>
                </div>
                <div className="flex flex-col items-center gap-1">
                  <BankerAvatar state="idle" persona="banker" size="sm" />
                  <span className="text-xs text-slate-400">Banker</span>
                </div>
                <div className="flex flex-col items-center gap-1">
                  <BankerAvatar state="speaking" persona="analyst" size="sm" mouthOpenness={60} />
                  <span className="text-xs text-slate-400">Speaking</span>
                </div>
                <div className="flex flex-col items-center gap-1">
                  <BankerAvatar state="confident" persona="banker" size="sm" />
                  <span className="text-xs text-slate-400">Confident</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Notification Settings */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 space-y-4">
        <h2 className="text-lg font-semibold text-white">Notification Limits</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-slate-400 block mb-1">
              Max Leads Per Cycle (1-10)
            </label>
            <input
              type="number"
              min={1}
              max={10}
              value={form.banker_max_leads_per_cycle}
              onChange={(e) =>
                setForm({ ...form, banker_max_leads_per_cycle: Number(e.target.value) })
              }
              className={inputClass}
            />
            <p className="text-xs text-slate-500 mt-0.5">
              How many top leads per discovery cycle generate notifications
            </p>
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1">
              Max Daily Notifications (1-100)
            </label>
            <input
              type="number"
              min={1}
              max={100}
              value={form.banker_max_daily_notifications}
              onChange={(e) =>
                setForm({ ...form, banker_max_daily_notifications: Number(e.target.value) })
              }
              className={inputClass}
            />
            <p className="text-xs text-slate-500 mt-0.5">
              Total notifications allowed per day across all cycles
            </p>
          </div>
        </div>
      </div>

      {/* Push Notifications */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 space-y-5">
        <div>
          <h2 className="text-lg font-semibold text-white">Push Notifications</h2>
          <p className="text-xs text-slate-400 mt-0.5">
            Your leads and position alerts will be sent to these channels. These are personal — only your own signals are sent here.
          </p>
        </div>

        {/* Microsoft Teams */}
        <div className="border border-slate-700/50 rounded-lg p-4 space-y-3">
          <h3 className="text-sm font-medium text-slate-200">Microsoft Teams</h3>
          <div>
            <label className="text-xs text-slate-400 block mb-1">Shared Webhook URL (fallback)</label>
            <div className="relative">
              <input
                type={showTeamsUrl ? 'text' : 'password'}
                value={form.teams_webhook_url ?? ''}
                onChange={(e) => setForm({ ...form, teams_webhook_url: e.target.value || null })}
                placeholder="https://outlook.office.com/webhook/..."
                className={inputClass + ' pr-10'}
              />
              <button
                type="button"
                onClick={() => setShowTeamsUrl(!showTeamsUrl)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                title={showTeamsUrl ? 'Hide URL' : 'Show URL'}
              >
                {showTeamsUrl ? '🙈' : '👁'}
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Incoming Webhook URL from your Teams channel Connectors. Used when no per-channel URL is set below. Leave empty to disable.
            </p>
          </div>
          <div className="space-y-3 border-t border-slate-700/30 pt-3">
            <p className="text-xs text-slate-400 font-medium">Per-channel URLs (optional — overrides shared URL)</p>
            <div>
              <label className="text-xs text-slate-400 block mb-1">Opportunity Leads channel</label>
              <div className="relative">
                <input
                  type={showTeamsLeadsUrl ? 'text' : 'password'}
                  value={form.teams_leads_webhook_url ?? ''}
                  onChange={(e) => setForm({ ...form, teams_leads_webhook_url: e.target.value || null })}
                  placeholder="https://outlook.office.com/webhook/... (optional)"
                  className={inputClass + ' pr-10'}
                />
                <button
                  type="button"
                  onClick={() => setShowTeamsLeadsUrl(!showTeamsLeadsUrl)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                  title={showTeamsLeadsUrl ? 'Hide URL' : 'Show URL'}
                >
                  {showTeamsLeadsUrl ? '🙈' : '👁'}
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-400 block mb-1">Position Alerts channel</label>
              <div className="relative">
                <input
                  type={showTeamsAlertsUrl ? 'text' : 'password'}
                  value={form.teams_alerts_webhook_url ?? ''}
                  onChange={(e) => setForm({ ...form, teams_alerts_webhook_url: e.target.value || null })}
                  placeholder="https://outlook.office.com/webhook/... (optional)"
                  className={inputClass + ' pr-10'}
                />
                <button
                  type="button"
                  onClick={() => setShowTeamsAlertsUrl(!showTeamsAlertsUrl)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                  title={showTeamsAlertsUrl ? 'Hide URL' : 'Show URL'}
                >
                  {showTeamsAlertsUrl ? '🙈' : '👁'}
                </button>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-xs text-slate-400">Send notifications for:</p>
            <label className={`flex items-center gap-2 cursor-pointer ${!form.teams_webhook_url && !form.teams_leads_webhook_url ? 'opacity-40 pointer-events-none' : ''}`}>
              <input
                type="checkbox"
                checked={form.teams_notify_leads}
                onChange={(e) => setForm({ ...form, teams_notify_leads: e.target.checked })}
                className="rounded"
              />
              <span className="text-sm text-slate-300">New investment leads</span>
            </label>
            <label className={`flex items-center gap-2 cursor-pointer ${!form.teams_webhook_url && !form.teams_alerts_webhook_url ? 'opacity-40 pointer-events-none' : ''}`}>
              <input
                type="checkbox"
                checked={form.teams_notify_alerts}
                onChange={(e) => setForm({ ...form, teams_notify_alerts: e.target.checked })}
                className="rounded"
              />
              <span className="text-sm text-slate-300">Position alerts (URGENT / ADVISORY)</span>
            </label>
          </div>
        </div>

        {/* Discord */}
        <div className="border border-slate-700/50 rounded-lg p-4 space-y-3">
          <h3 className="text-sm font-medium text-slate-200">Discord</h3>
          <div>
            <label className="text-xs text-slate-400 block mb-1">Shared Webhook URL (fallback)</label>
            <div className="relative">
              <input
                type={showDiscordUrl ? 'text' : 'password'}
                value={form.discord_webhook_url ?? ''}
                onChange={(e) => setForm({ ...form, discord_webhook_url: e.target.value || null })}
                placeholder="https://discord.com/api/webhooks/..."
                className={inputClass + ' pr-10'}
              />
              <button
                type="button"
                onClick={() => setShowDiscordUrl(!showDiscordUrl)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                title={showDiscordUrl ? 'Hide URL' : 'Show URL'}
              >
                {showDiscordUrl ? '🙈' : '👁'}
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Webhook URL from your Discord channel Integrations. Used when no per-channel URL is set below. Leave empty to disable.
            </p>
          </div>
          <div className="space-y-3 border-t border-slate-700/30 pt-3">
            <p className="text-xs text-slate-400 font-medium">Per-channel URLs (optional — overrides shared URL)</p>
            <div>
              <label className="text-xs text-slate-400 block mb-1">Opportunity Leads channel</label>
              <div className="relative">
                <input
                  type={showDiscordLeadsUrl ? 'text' : 'password'}
                  value={form.discord_leads_webhook_url ?? ''}
                  onChange={(e) => setForm({ ...form, discord_leads_webhook_url: e.target.value || null })}
                  placeholder="https://discord.com/api/webhooks/... (optional)"
                  className={inputClass + ' pr-10'}
                />
                <button
                  type="button"
                  onClick={() => setShowDiscordLeadsUrl(!showDiscordLeadsUrl)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                  title={showDiscordLeadsUrl ? 'Hide URL' : 'Show URL'}
                >
                  {showDiscordLeadsUrl ? '🙈' : '👁'}
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-400 block mb-1">Position Alerts channel</label>
              <div className="relative">
                <input
                  type={showDiscordAlertsUrl ? 'text' : 'password'}
                  value={form.discord_alerts_webhook_url ?? ''}
                  onChange={(e) => setForm({ ...form, discord_alerts_webhook_url: e.target.value || null })}
                  placeholder="https://discord.com/api/webhooks/... (optional)"
                  className={inputClass + ' pr-10'}
                />
                <button
                  type="button"
                  onClick={() => setShowDiscordAlertsUrl(!showDiscordAlertsUrl)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                  title={showDiscordAlertsUrl ? 'Hide URL' : 'Show URL'}
                >
                  {showDiscordAlertsUrl ? '🙈' : '👁'}
                </button>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-xs text-slate-400">Send notifications for:</p>
            <label className={`flex items-center gap-2 cursor-pointer ${!form.discord_webhook_url && !form.discord_leads_webhook_url ? 'opacity-40 pointer-events-none' : ''}`}>
              <input
                type="checkbox"
                checked={form.discord_notify_leads}
                onChange={(e) => setForm({ ...form, discord_notify_leads: e.target.checked })}
                className="rounded"
              />
              <span className="text-sm text-slate-300">New investment leads</span>
            </label>
            <label className={`flex items-center gap-2 cursor-pointer ${!form.discord_webhook_url && !form.discord_alerts_webhook_url ? 'opacity-40 pointer-events-none' : ''}`}>
              <input
                type="checkbox"
                checked={form.discord_notify_alerts}
                onChange={(e) => setForm({ ...form, discord_notify_alerts: e.target.checked })}
                className="rounded"
              />
              <span className="text-sm text-slate-300">Position alerts (URGENT / ADVISORY)</span>
            </label>
          </div>
        </div>
      </div>

      {/* Archive Settings */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 space-y-4">
        <h2 className="text-lg font-semibold text-white">Archive Settings</h2>
        <div>
          <label className="text-xs text-slate-400 block mb-1">
            Lead Archive After (days, 1-30)
          </label>
          <input
            type="number"
            min={1}
            max={30}
            value={form.banker_lead_archive_days}
            onChange={(e) =>
              setForm({ ...form, banker_lead_archive_days: Number(e.target.value) })
            }
            className={inputClass}
          />
          <p className="text-xs text-slate-500 mt-0.5">
            Leads older than this are auto-archived to keep the active list fresh
          </p>
        </div>
      </div>
      {/* IPO & Launch Discovery — admin only */}
      <PermissionGate permission="admin:config-write">
        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">IPO &amp; Launch Discovery</h2>
            <button
              onClick={handleIpoSave}
              disabled={ipoConfigMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-500 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
            >
              {ipoSaved ? <Check size={14} /> : <Save size={14} />}
              {ipoSaved ? 'Saved' : 'Save IPO Settings'}
            </button>
          </div>

          <div>
            <label className="text-xs text-slate-400 block mb-2">Data Providers</label>
            <div className="space-y-3">
              {ALL_IPO_PROVIDERS.map(provider => {
                const info = PROVIDER_INFO[provider];
                const keyField = info?.keyField as keyof typeof ipoForm | undefined;
                return (
                  <div key={provider} className="bg-slate-900/50 rounded-lg p-3 space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={ipoForm.ipo_providers_enabled.includes(provider)}
                        onChange={() => toggleIpoProvider(provider)}
                        className="rounded"
                      />
                      <span className="text-sm text-slate-300 capitalize font-medium">{provider}</span>
                      <span className="text-xs text-slate-500 ml-auto">{info?.hint}</span>
                    </label>
                    {info?.needsKey && keyField && (
                      <div className="mt-1">
                        {String(ipoForm[keyField] ?? '').startsWith('••••') ? (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded">Configured ({String(ipoForm[keyField]).slice(-4)})</span>
                            <button
                              type="button"
                              onClick={() => setIpoForm({ ...ipoForm, [keyField]: '' })}
                              className="text-xs text-slate-500 hover:text-slate-300"
                            >
                              Change
                            </button>
                          </div>
                        ) : (
                          <input
                            type="password"
                            placeholder={`Enter ${provider} API key`}
                            value={String(ipoForm[keyField] ?? '')}
                            onChange={(e) => setIpoForm({ ...ipoForm, [keyField]: e.target.value })}
                            className={inputClass}
                          />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-400 block mb-2">Asset Types</label>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={ipoForm.ipo_stock_enabled}
                  onChange={(e) => setIpoForm({ ...ipoForm, ipo_stock_enabled: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm text-slate-300">Stock IPOs</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={ipoForm.ipo_crypto_enabled}
                  onChange={(e) => setIpoForm({ ...ipoForm, ipo_crypto_enabled: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm text-slate-300">Crypto Launches</span>
              </label>
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-400 block mb-1">Cache TTL (hours)</label>
            <input
              type="number"
              min={1}
              max={48}
              value={ipoForm.ipo_cache_ttl_hours}
              onChange={(e) => setIpoForm({ ...ipoForm, ipo_cache_ttl_hours: Number(e.target.value) })}
              className={inputClass}
            />
            <p className="text-xs text-slate-500 mt-0.5">
              How long to cache IPO data from providers (default: 6h)
            </p>
          </div>
        </div>
      </PermissionGate>

    </div>
    </PermissionGate>
  );
};

export default BankerSettings;
